import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { CheckCircle, ErrorOutline } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { InkyWorking } from "./brand";
import { AuthLayout, AuthCard } from "./pagekit";

/**
 * Reads an auth callback parameter from the query string or hash fragment.
 * @param {URLSearchParams} searchParams - URL search parameters.
 * @param {URLSearchParams} hashParams - URL hash parameters.
 * @param {string} name - Parameter name to read.
 * @returns {string | null} Parameter value when present.
 */
const getCallbackParam = (searchParams, hashParams, name) => {
  return searchParams.get(name) || hashParams.get(name);
};

/**
 * Completes Supabase email confirmation before redirecting to protected app pages.
 * @returns {JSX.Element} Email confirmation status page.
 */
const EmailConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("Verifying your email...");

  useEffect(() => {
    let isMounted = true;

    /**
     * Exchanges confirmation URL parameters for a Supabase session.
     * @returns {Promise<void>} Resolves after navigation or error state update.
     */
    const confirmEmail = async () => {
      const searchParams = new URLSearchParams(location.search);
      const hashParams = new URLSearchParams((location.hash || "").replace(/^#/, ""));
      const errorCode = getCallbackParam(searchParams, hashParams, "error_code");
      const errorDescription = getCallbackParam(
        searchParams,
        hashParams,
        "error_description"
      );

      if (errorCode) {
        throw new Error(
          errorDescription || "This confirmation link is invalid or expired."
        );
      }

      const code = getCallbackParam(searchParams, hashParams, "code");
      const accessToken = getCallbackParam(searchParams, hashParams, "access_token");
      const refreshToken = getCallbackParam(searchParams, hashParams, "refresh_token");
      const tokenHash = getCallbackParam(searchParams, hashParams, "token_hash");
      const type = getCallbackParam(searchParams, hashParams, "type") || "signup";

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
      } else if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) throw error;
      } else if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });
        if (error) throw error;
      }

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;
      if (!session) {
        throw new Error("Email verified, but no session was created. Please sign in.");
      }

      if (!isMounted) return;
      setStatus("success");
      setMessage("Email verified. Redirecting to your projects...");
      window.history.replaceState({}, document.title, "/auth/confirm");
      setTimeout(() => navigate("/projects", { replace: true }), 800);
    };

    confirmEmail().catch((confirmationError) => {
      if (!isMounted) return;
      setStatus("error");
      setMessage(
        confirmationError.message ||
          "We could not verify this email confirmation link."
      );
    });

    return () => {
      isMounted = false;
    };
  }, [location.hash, location.search, navigate]);

  const title = status === "error"
    ? "We could not confirm your email"
    : status === "success"
      ? "Email confirmed"
      : "Confirming your email";

  return (
    <AuthLayout>
      <AuthCard
        icon={
          status === "verifying" ? (
            <InkyWorking label="Checking your link" mood="thinking" />
          ) : status === "success" ? (
            <CheckCircle sx={{ fontSize: 56, color: "success.main" }} />
          ) : (
            <ErrorOutline sx={{ fontSize: 56, color: "error.main" }} />
          )
        }
        title={title}
      >
        <Box role="status" aria-live="polite">
          {status === "verifying" ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              {message}
            </Typography>
          ) : (
            <Alert severity={status === "error" ? "error" : "success"}>{message}</Alert>
          )}
        </Box>
        {status === "error" && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            onClick={() => navigate("/login", { replace: true })}
          >
            Back to sign in
          </Button>
        )}
      </AuthCard>
    </AuthLayout>
  );
};

export default EmailConfirmation;
